/**
 * Serviço para gerenciar sessão no login app.
 * - Produção em *.imobdesk.com.br: memória (evita persistir token no browser)
 * - Localhost/dev: localStorage como fallback para DX
 */

const ACCESS_TOKEN_KEY = 'imobdesk_access_token'
const IS_NEW_USER_KEY = 'imobdesk_is_new_user'
const SESSION_HINT_KEY = 'imobdesk_session_active'
let accessTokenCache: string | null = null
let initialized = false
let initializePromise: Promise<void> | null = null

function isSecureProdDomain(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname.toLowerCase()
  return host.endsWith('.imobdesk.com.br')
}

function canPersistAccessTokenInLocalStorage(): boolean {
  return !isSecureProdDomain()
}

function getLocalStorageValue(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch (_error) {
    return null
  }
}

function setLocalStorageValue(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch (_error) {}
}

function removeLocalStorageValue(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch (_error) {}
}

export async function initializeTokenStorage(): Promise<void> {
  if (initialized) return
  if (initializePromise) {
    await initializePromise
    return
  }

  initializePromise = (async () => {
    try {
      if (canPersistAccessTokenInLocalStorage()) {
        // Não sobrescreve token já salvo nesta sessão (ex.: magic link).
        if (accessTokenCache == null) {
          accessTokenCache = getLocalStorageValue(ACCESS_TOKEN_KEY)
        }
      } else {
        removeLocalStorageValue(ACCESS_TOKEN_KEY)
        // Em produção o token fica só em memória — nunca zerar se já houver.
      }
    } finally {
      initialized = true
    }
  })()

  try {
    await initializePromise
  } finally {
    initializePromise = null
  }
}

void initializeTokenStorage()

/**
 * Salva o access token no localStorage
 */
export function saveAccessToken(token: string): void {
  accessTokenCache = token
  initialized = true
  markSessionHint()

  if (canPersistAccessTokenInLocalStorage()) {
    setLocalStorageValue(ACCESS_TOKEN_KEY, token)
  } else {
    removeLocalStorageValue(ACCESS_TOKEN_KEY)
  }
}

/**
 * Obtém o access token do localStorage
 */
export function getAccessToken(): string | null {
  if (accessTokenCache) {
    return accessTokenCache
  }

  if (canPersistAccessTokenInLocalStorage()) {
    const stored = getLocalStorageValue(ACCESS_TOKEN_KEY)
    if (stored) {
      accessTokenCache = stored
      return stored
    }
  }

  return null
}

export async function getAccessTokenAsync(): Promise<string | null> {
  await initializeTokenStorage()
  return getAccessToken()
}

/** Mantido por compatibilidade; apenas access token é persistido no frontend. */
export function saveTokens(accessToken: string, _refreshToken?: string): void {
  saveAccessToken(accessToken)
}

/**
 * Remove ambos os tokens do localStorage
 */
export function clearTokens(): void {
  accessTokenCache = null
  initialized = true
  removeLocalStorageValue(ACCESS_TOKEN_KEY)
  clearSessionHint()
}

/** Marca que há sessão (cookie de refresh esperado) — evita refresh 401 no cold start. */
export function markSessionHint(): void {
  try {
    sessionStorage.setItem(SESSION_HINT_KEY, '1')
  } catch {
    // ignore
  }
}

export function clearSessionHint(): void {
  try {
    sessionStorage.removeItem(SESSION_HINT_KEY)
  } catch {
    // ignore
  }
}

export function hasSessionHint(): boolean {
  if (getAccessToken()) return true
  try {
    return sessionStorage.getItem(SESSION_HINT_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Verifica se existe um token de acesso salvo
 */
export function hasAccessToken(): boolean {
  return getAccessToken() !== null
}

/**
 * Chave para armazenar flag de usuário novo
 */
/**
 * Marca o usuário como novo (após cadastro)
 */
export function markUserAsNew(): void {
  setLocalStorageValue(IS_NEW_USER_KEY, 'true')
}

/**
 * Verifica se o usuário é novo
 */
export function isNewUser(): boolean {
  return getLocalStorageValue(IS_NEW_USER_KEY) === 'true'
}

/**
 * Remove a flag de usuário novo (após criar dashboard ou outra ação)
 */
export function clearNewUserFlag(): void {
  removeLocalStorageValue(IS_NEW_USER_KEY)
}

/** Apenas para testes — reseta cache em memória entre casos. */
export function resetTokenStorageForTests(): void {
  accessTokenCache = null
  initialized = false
  initializePromise = null
  clearSessionHint()
}

